import React, { useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

interface IdPhotoThumbProps {
  /** 已保存照片的下载地址（鉴权接口），没有照片/无权限查看时为空 */
  url?: string | null;
  fetchBlob: () => Promise<Blob>;
  onClick: () => void;
  /** 没有照片时，占位框上要不要显示提示（比如"无权限查看或未上传"） */
  emptyHint?: string;
  size?: number;
}

/**
 * 列表里用的只读证件照片缩略图：有照片时拉取字节内容渲染小图，点击查看大图；
 * 没有照片时渲染一个同样大小的虚线占位框，保证"正面/背面"始终各占一个固定位置。
 */
const IdPhotoThumb: React.FC<IdPhotoThumbProps> = ({ url, fetchBlob, onClick, emptyHint, size = 32 }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setBlobUrl(null);
    if (!url) return;
    setLoading(true);
    fetchBlob()
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const boxStyle: React.CSSProperties = {
    width: size, height: size, borderRadius: 4,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#fafafa', overflow: 'hidden', flexShrink: 0,
  };

  if (!url) {
    const placeholder = (
      <div style={{ ...boxStyle, border: '1px dashed #d9d9d9', color: '#d9d9d9', fontSize: 10 }}>-</div>
    );
    return emptyHint ? <Tooltip title={emptyHint}>{placeholder}</Tooltip> : placeholder;
  }

  return (
    <div style={{ ...boxStyle, border: '1px solid #d9d9d9', cursor: 'pointer' }} onClick={onClick}>
      {loading || !blobUrl ? <LoadingOutlined style={{ fontSize: 12 }} /> : (
        <img src={blobUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
    </div>
  );
};

export default IdPhotoThumb;
